import { Field, Form, Formik } from 'formik'
import React from 'react'
import * as Yup from 'yup'

import {
  Button,
  ButtonGroup,
  Fieldset,
  Input,
  ModalBox,
  useMediaQueryContext,
  useModalContext,
} from '@fourthwall/components'

import { useOrderGroupedByShipmentsQuery } from '../../queries/useOrderGroupedByShipmentsQuery'
import { useUpdateEmailMutation } from '../../queries/useUpdateEmailMutation'

type Values = {
  email: string
}

export type OrderEmailModalProps = {
  orderId: string
  initialValues: Values
}

const ValidationSchema: Yup.SchemaOf<Values> = Yup.object().shape({
  email: Yup.string().email().required(),
})

export const OrderEmailModal = ({ orderId, initialValues }: OrderEmailModalProps) => {
  const media = useMediaQueryContext()
  const { close } = useModalContext()
  const { invalidateOrderGroupedByShipmentsQuery } = useOrderGroupedByShipmentsQuery([{ orderId }])

  const { updateEmailMutation } = useUpdateEmailMutation({
    onSuccess: () => {
      invalidateOrderGroupedByShipmentsQuery()
    },
    onSettled: () => {
      close()
    },
  })

  const handleSubmit = async (values: Values) => {
    await updateEmailMutation.mutateAsync([{ orderId, email: values.email }])
  }

  return (
    <ModalBox title="Edit email address" onClose={close}>
      <Formik<Values>
        initialValues={initialValues}
        validationSchema={ValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <Fieldset.Row>
              <Fieldset.Item>
                <Field name="email" component={Input} label="Email" fluid />
              </Fieldset.Item>
            </Fieldset.Row>
            <div style={{ marginTop: media.tablet ? 40 : 16 }}>
              <ButtonGroup
                space="medium"
                justify={media.tablet ? 'right' : undefined}
                direction={media.tablet ? 'row' : 'column-reverse'}
                fullWidth={!media.tablet}
              >
                <Button
                  label="Cancel"
                  disabled={isSubmitting}
                  fullWidth={!media.tablet}
                  onClick={close}
                />
                <Button
                  type="submit"
                  label="Update email address"
                  appearance="primary"
                  fullWidth={!media.tablet}
                  loading={isSubmitting}
                />
              </ButtonGroup>
            </div>
          </Form>
        )}
      </Formik>
    </ModalBox>
  )
}
